import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { createElement } from 'react';
import { getSession, logout as logoutFn, type AuthUser } from '../lib/auth';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  isSignedIn: boolean;
  isSubscriber: boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Provider for the shared auth state. Must wrap any tree that calls
 * useAuth(). The state lives here once so a refreshUser() from one
 * subscriber (e.g. BuilderWizard after a token spend) reaches every
 * other subscriber (AppHeader's balance, AccountPage's summary) on the
 * next render, not just the component that triggered it.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSession().then(u => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  const logout = useCallback(async () => {
    await logoutFn();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const u = await getSession();
    setUser(u);
  }, []);

  const value: AuthContextValue = {
    user,
    loading,
    isSignedIn: !!user,
    isSubscriber: user?.is_subscriber === 1,
    logout,
    refreshUser,
  };

  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside an <AuthProvider>');
  }
  return ctx;
}
